import { useEffect, useRef, useState } from "react";

// Mouvement = CSS + IntersectionObserver uniquement. `prefers-reduced-motion: reduce`
// coupe tout : les valeurs finales s'affichent directement.
export function reducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

export function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(() => reducedMotion() || typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: "0px 0px -8% 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

const COUNTED_KEY = "rr.home.counted";

// 0 → valeur en 800 ms, easing out, sans rebond. Une fois par session : la navigation SPA ne rejoue pas.
export function useCountUp(value, duration = 800) {
  const already = reducedMotion() || sessionStorage.getItem(COUNTED_KEY) === "1";
  const [n, setN] = useState(already ? value : 0);
  useEffect(() => {
    if (already || value == null) { setN(value); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick); else sessionStorage.setItem(COUNTED_KEY, "1");
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, already]);
  return n ?? 0;
}

export function relativeTime(iso, lang, now = Date.now()) {
  if (!iso) return "";
  const diff = Math.max(0, now - new Date(iso).getTime());
  const m = Math.round(diff / 60000), h = Math.round(diff / 3600000), d = Math.round(diff / 86400000);
  const fr = lang === "fr";
  if (m < 1) return fr ? "à l'instant" : "just now";
  if (m < 60) return fr ? `il y a ${m} min` : `${m} min ago`;
  if (h < 48) return fr ? `il y a ${h} h` : `${h} h ago`;
  return fr ? `il y a ${d} j` : `${d} d ago`;
}

export function countdown(iso, lang, now = Date.now()) {
  const diff = new Date(iso).getTime() - now;
  if (diff <= 0) return lang === "fr" ? "en cours" : "now";
  const h = Math.floor(diff / 3600000), d = Math.floor(h / 24);
  if (d >= 1) return lang === "fr" ? `dans ${d} j ${h % 24} h` : `in ${d}d ${h % 24}h`;
  return lang === "fr" ? `dans ${h} h ${Math.floor((diff % 3600000) / 60000)} min` : `in ${h}h ${Math.floor((diff % 3600000) / 60000)}m`;
}

const DISMISS_KEY = "rr.home.dismissed";
export function dismissed(id) { return (sessionStorage.getItem(DISMISS_KEY) || "").split(",").includes(id); }
export function dismiss(id) { sessionStorage.setItem(DISMISS_KEY, [...(sessionStorage.getItem(DISMISS_KEY) || "").split(",").filter(Boolean), id].join(",")); }
